import { isAxiosError } from 'axios';

// Rejects with AbortError if the signal is canceled before the delay ends.
export function waitWithSignal(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort);
  });
}

export function isRequestAborted(error: unknown): boolean {
  if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
    return true;
  }

  return error instanceof Error && error.name === 'AbortError';
}

// Resolves once the tab is visible; rejects with AbortError if canceled while hidden.
export function waitForDocumentVisible(signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    return Promise.reject(new DOMException('Aborted', 'AbortError'));
  }

  if (typeof document === 'undefined' || !document.hidden) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      signal.removeEventListener('abort', onAbort);
    };
    const onVisibilityChange = () => {
      if (!document.hidden) {
        cleanup();
        resolve();
      }
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    signal.addEventListener('abort', onAbort);
  });
}
